import React from 'react'
import MetricCard from '../../../common/components/atom/MetricCard/MetricCard'
import {
  VoltageIcon,
  CurrentIcon,
  FrequencyIcon,
  PowerFactorIcon,
} from '../../../common/components/atom/MetricCard/icons'
import {
  useLuminaireActivePower,
  useLuminairePowerFactor,
  useLuminaireActiveEnergy,
  useLuminaireDimmingLevel,
  useLuminaireDownlinkRssi,
  useLuminaireFirmwareVersion,
} from '../../../features/luminaire/hooks/useLuminaireMetrics'
import { LuminaireMetricFilter } from '../../../features/luminaire/types/luminaire.types'
import { lastPoint, formatMetricValue, formatMetricDate } from '../../../common/utils/metricFormat'
import styles from './LuminaireLastValuesMetrics.module.scss'

interface Props {
  devEui: string
  filter: LuminaireMetricFilter
}

const getStatus = (
  type: string,
  value: number | undefined
): 'ok' | 'warning' | 'danger' => {
  if (value === undefined || value === null) return 'danger'
  switch (type) {
    case 'powerFactor':
      if (value >= 0.9 && value <= 1.0) return 'ok'
      if (value >= 0.8 && value < 0.9) return 'warning'
      return 'danger'
    case 'rssi':
      if (value >= -90) return 'ok'
      if (value >= -110) return 'warning'
      return 'danger'
    case 'dimming':
      return 'ok'
    default:
      return 'ok'
  }
}

export default function LuminaireLastValuesMetrics({ devEui, filter }: Props) {
  // Cartes "Dernières valeurs" : on demande explicitement le dernier point au
  // backend (last=true) plutôt que de filtrer le range comme pour les courbes,
  // car le device peut avoir été silencieux dans la fenêtre choisie. Le backend
  // glisse jusqu'à 1 an avant de renoncer.
  const lastFilter = { ...filter, last: true }
  const { activePowerQuery } = useLuminaireActivePower(devEui, lastFilter)
  const { powerFactorQuery } = useLuminairePowerFactor(devEui, lastFilter)
  const { activeEnergyQuery } = useLuminaireActiveEnergy(devEui, lastFilter)
  const { dimmingLevelQuery } = useLuminaireDimmingLevel(devEui, lastFilter)
  const { downlinkRssiQuery } = useLuminaireDownlinkRssi(devEui, lastFilter)
  const firmwareQuery = useLuminaireFirmwareVersion(devEui, lastFilter)

  const lastActivePower = lastPoint(activePowerQuery.data)
  const lastPowerFactor = lastPoint(powerFactorQuery.data)
  const lastActiveEnergy = lastPoint(activeEnergyQuery.data)
  const lastDimming = lastPoint(dimmingLevelQuery.data)
  const lastRssi = lastPoint(downlinkRssiQuery.data)
  const lastFirmware = lastPoint(firmwareQuery.data)

  return (
    <div className={styles.lastValuesContainer}>
      <div className={styles.metricsGrid}>
        <MetricCard
          title="Puissance active"
          value={activePowerQuery.isLoading ? '...' : formatMetricValue(lastActivePower?.Value, 2)}
          unit="W"
          icon={<PowerFactorIcon />}
          status={getStatus('activePower', lastActivePower?.Value as number)}
          showPercentage={false}
          showIcon={false}
          tooltip={
            activePowerQuery.isLoading
              ? undefined
              : `Capturé le: ${formatMetricDate(lastActivePower?.Time)}`
          }
        />

        <MetricCard
          title="Facteur de puissance"
          value={powerFactorQuery.isLoading ? '...' : formatMetricValue(lastPowerFactor?.Value, 2)}
          unit=""
          icon={<PowerFactorIcon />}
          status={getStatus('powerFactor', lastPowerFactor?.Value as number)}
          showPercentage={false}
          showIcon={false}
          tooltip={
            powerFactorQuery.isLoading
              ? undefined
              : `Capturé le: ${formatMetricDate(lastPowerFactor?.Time)}`
          }
        />

        <MetricCard
          title="Énergie active"
          value={activeEnergyQuery.isLoading ? '...' : formatMetricValue(lastActiveEnergy?.Value, 2)}
          unit="kWh"
          icon={<VoltageIcon />}
          status="ok"
          showPercentage={false}
          showIcon={false}
          tooltip={
            activeEnergyQuery.isLoading
              ? undefined
              : `Capturé le: ${formatMetricDate(lastActiveEnergy?.Time)}`
          }
        />

        <MetricCard
          title="Dimming level"
          value={dimmingLevelQuery.isLoading ? '...' : formatMetricValue(lastDimming?.Value, 0)}
          unit="%"
          icon={<CurrentIcon />}
          status={getStatus('dimming', lastDimming?.Value as number)}
          showPercentage={false}
          showIcon={false}
          tooltip={
            dimmingLevelQuery.isLoading
              ? undefined
              : `Capturé le: ${formatMetricDate(lastDimming?.Time)}`
          }
        />

        <MetricCard
          title="RSSI downlink"
          value={downlinkRssiQuery.isLoading ? '...' : formatMetricValue(lastRssi?.Value, 0)}
          unit="dBm"
          icon={<FrequencyIcon />}
          status={getStatus('rssi', lastRssi?.Value as number)}
          showPercentage={false}
          showIcon={false}
          tooltip={
            downlinkRssiQuery.isLoading
              ? undefined
              : `Capturé le: ${formatMetricDate(lastRssi?.Time)}`
          }
        />

        <MetricCard
          title="Firmware"
          value={firmwareQuery.isLoading ? '...' : (lastFirmware?.Value as string) ?? 'NA'}
          unit=""
          icon={<PowerFactorIcon />}
          status="ok"
          showPercentage={false}
          showIcon={false}
          tooltip={
            firmwareQuery.isLoading
              ? undefined
              : `Capturé le: ${formatMetricDate(lastFirmware?.Time)}`
          }
        />
      </div>
    </div>
  )
}
